import { useCallback, useEffect, useRef, useState } from 'react';
import { apiService, KIOSK_AUTH_TOKEN_CHANGED_EVENT } from '@/services/apiService';
import BroadcastMessageBody from '@/components/BroadcastMessageBody';
import { Button, Dialog } from '@/components/ui';

type PopupRow = {
  id: string;
  title: string;
  body: string;
  createdAt: string | null;
  displayMode?: 'once' | 'sticky_daily';
  expiresAt?: string | null;
};

/**
 * In-app popups: `once` uses OK to mark seen on server; `sticky_daily` is re-fetched each load (shows again
 * after reload) until expiresAt; OK only hides for the current page session; Don't show again (server).
 * The Dialog is not dismissible: only OK / Don't show again close it (no close button, Escape or backdrop).
 */
export default function KioskInAppPopupGate() {
  const [queue, setQueue] = useState<PopupRow[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [marking, setMarking] = useState(false);
  const batchTotalRef = useRef(0);

  const load = useCallback(async () => {
    // Avoid noisy 401s when user is not authenticated yet.
    // The gate will re-run automatically when the token changes (login/logout).
    if (!apiService.getAccessToken()) {
      batchTotalRef.current = 0;
      setQueue([]);
      setActiveIndex(0);
      return;
    }
    try {
      const res = await apiService.getMyUnseenInAppPopups();
      if (res.success && res.data?.popups?.length) {
        const list = res.data.popups;
        batchTotalRef.current = list.length;
        setQueue(list);
        setActiveIndex(0);
      } else {
        batchTotalRef.current = 0;
        setQueue([]);
        setActiveIndex(0);
      }
    } catch {
      batchTotalRef.current = 0;
      setQueue([]);
      setActiveIndex(0);
    }
  }, []);

  useEffect(() => {
    void load();
    const onAuth = () => void load();
    window.addEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, onAuth);
    return () => window.removeEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, onAuth);
  }, [load]);

  const current = queue[activeIndex];
  const isSticky = current?.displayMode === 'sticky_daily';

  const removeCurrentFromQueue = () => {
    setQueue((prev) => {
      const next = prev.filter((_, i) => i !== activeIndex);
      setActiveIndex((idx) => {
        if (next.length === 0) return 0;
        return Math.min(idx, next.length - 1);
      });
      return next;
    });
  };

  const onMarkSeen = async () => {
    if (!current || marking || isSticky) return;
    setMarking(true);
    try {
      const res = await apiService.markInAppPopupSeen(current.id);
      if (res.success) removeCurrentFromQueue();
    } finally {
      setMarking(false);
    }
  };

  const onCloseSticky = () => {
    if (!current || marking || !isSticky) return;
    removeCurrentFromQueue();
  };

  const onNeverAgain = async () => {
    if (!current || marking || !isSticky) return;
    setMarking(true);
    try {
      const res = await apiService.markInAppPopupNeverAgain(current.id);
      if (res.success) removeCurrentFromQueue();
    } finally {
      setMarking(false);
    }
  };

  if (!current) return null;

  const batchTotal = batchTotalRef.current;
  const positionInBatch = activeIndex + 1;

  return (
    <Dialog
      open
      onClose={() => {}}
      dismissible={false}
      title={current.title || 'Announcement'}
      description={batchTotal > 1 ? `${positionInBatch} of ${batchTotal}` : undefined}
      size="xl"
      placement="center"
      footer={
        isSticky ? (
          <>
            <Button variant="ghost" size="sm" onClick={() => void onNeverAgain()} disabled={marking}>
              Don&apos;t show this again
            </Button>
            <Button variant="primary" onClick={() => onCloseSticky()} disabled={marking}>
              OK
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={() => void onMarkSeen()} disabled={marking}>
            {marking ? 'Saving...' : 'OK'}
          </Button>
        )
      }
    >
      {isSticky && current.expiresAt ? (
        <p className="m-0 mb-3 font-ui text-small text-tertiary">
          Ends{' '}
          {new Date(current.expiresAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
        </p>
      ) : null}
      <div className="whitespace-pre-wrap font-ui text-body text-primary">
        <BroadcastMessageBody
          text={current.body}
          linkClassName="break-all font-medium text-link underline underline-offset-2 hover:text-primary"
        />
      </div>
    </Dialog>
  );
}
